import { useState, useEffect, useRef } from 'react';
import { Button } from '../components/ui/button';
import { ExternalLink, Wrench, RefreshCw } from 'lucide-react';

const SHOP_ORDERS_URL = 'https://rogers-shop-workorder.vercel.app';

export default function ShopOrdersPage() {
  const [iframeBlocked, setIframeBlocked] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const iframeRef = useRef(null);
  const loadedRef = useRef(false);

  // Detect if the iframe never finishes loading (likely blocked by X-Frame-Options/CSP)
  useEffect(() => {
    loadedRef.current = false;
    const timer = setTimeout(() => {
      if (!loadedRef.current) {
        setIframeBlocked(true);
      }
    }, 6000);
    return () => clearTimeout(timer);
  }, [reloadKey]);

  return (
    <div className="h-full flex flex-col" data-testid="shop-orders-page">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <div className="flex items-center gap-2">
          <Wrench className="h-5 w-5 text-primary" />
          <h1 className="text-lg font-bold">Shop Orders</h1>
          <span className="text-xs text-muted-foreground hidden sm:inline">— rogers-shop-workorder.vercel.app</span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { setIframeBlocked(false); setReloadKey(k => k + 1); }}
            data-testid="shop-orders-reload"
            title="Reload"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
          <a
            href={SHOP_ORDERS_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="shop-orders-new-tab"
          >
            <Button variant="outline" size="sm">
              <ExternalLink className="h-4 w-4 mr-2" />
              Open in new tab
            </Button>
          </a>
        </div>
      </div>

      <div className="flex-1 relative bg-muted/20">
        {iframeBlocked ? (
          <div className="flex flex-col items-center justify-center h-full p-8 text-center">
            <Wrench className="h-12 w-12 text-muted-foreground mb-4" />
            <h2 className="text-xl font-semibold mb-2">Couldn't embed the Shop Orders site</h2>
            <p className="text-sm text-muted-foreground max-w-md mb-6">
              The Shop Orders web app blocks embedded loading. Click below to open it in a new tab —
              when you're done you can come back here.
            </p>
            <a href={SHOP_ORDERS_URL} target="_blank" rel="noopener noreferrer">
              <Button data-testid="shop-orders-fallback-open">
                <ExternalLink className="h-4 w-4 mr-2" />
                Open Shop Orders
              </Button>
            </a>
          </div>
        ) : (
          <iframe
            key={reloadKey}
            ref={iframeRef}
            title="Rogers Shop Work Orders"
            src={SHOP_ORDERS_URL}
            className="w-full h-full border-0"
            onLoad={() => { loadedRef.current = true; }}
            allow="clipboard-write; clipboard-read"
            data-testid="shop-orders-iframe"
          />
        )}
      </div>
    </div>
  );
}
